import Store from '@ember-data/store';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  JournalRow,
  type onJournalCellChangeCallback,
} from 'donor-elf-web/lib/journal-utils';
import RecommendationModel from 'donor-elf-web/models/recommendation';
import AddSomethingModal from 'donor-elf-web/services/add-something-modal';
import Changeset from 'ember-changeset';

interface ComponentArgs {
  journalRow: JournalRow;
  allSegments: string[];
  onGroupClicked: (groupName: string) => void;
  onSegmentClicked: (segmentName: string) => void;
  onRemoveJournalRow: (journalRow: JournalRow) => void;
  onRecommendedBySaved: (recommendation: RecommendationModel) => void;
  onChange: onJournalCellChangeCallback;
}

export default class JournalCellContactIndex extends Component<ComponentArgs> {
  @service declare addSomethingModal: AddSomethingModal;
  @service declare store: Store;

  @tracked removing = false;
  // allows us to manually open the dropdown later
  // there used to be multiple dropdowns but now only 1
  dropdowns: any = { ask: undefined, recommendedBy: undefined };

  get contact() {
    return this.args.journalRow.journalContactInfo.contact;
  }

  get journalContactInfo() {
    return this.args.journalRow.journalContactInfo;
  }

  get journalContact() {
    return this.args.journalRow.journalContactInfo.journalContact;
  }

  get cellState() {
    return this.args.journalRow.cellState['contact'];
  }

  get changeset() {
    if (this.cellState.changeset == null) {
      this.cellState.changeset = new Changeset(
        this.args.journalRow.journalContactInfo.journalContact,
      );
    }
    return this.cellState.changeset;
  }

  get pledge() {
    return this.journalContactInfo.pledges[0];
  }

  get canAddRecommendedBy() {
    return (this.journalContactInfo.recommendedByContactIds || []).length === 0;
  }

  @action
  saveDropdown(dropdownName: any, dropdown: any) {
    this.dropdowns[dropdownName] = dropdown;
  }

  @action
  openDropdown(dropdownName: any) {
    this.dropdowns[dropdownName]?.actions.open();
  }

  @action
  toggleShowTasks() {
    this.args.journalRow.showTasks = !this.args.journalRow.showTasks;
  }

  @action
  showRemoveConfirmation() {
    this.cellState.showRemoveConfirmation = true;
    this.cellState.removePledge = false;
    this.args.journalRow.fireCellStateChanged();
  }

  @action
  setCellState(property: any, val: any) {
    this.cellState[property] = val;
    this.args.journalRow.fireCellStateChanged();
  }

  @action
  handleNotesChanged(e: any) {
    this.journalContact.notes = e.target.value;
    this.save();
  }

  @action
  handleAddRecommendation() {
    this.addSomethingModal.open({
      type: 'contact',
      contactOptions: { recommendedByContactId: this.contact.id },
    });
  }

  @action
  async save() {
    await this.journalContact.save();
  }

  @action
  async remove() {
    if (this.removing) {
      return;
    }
    this.removing = true;
    let removeContact = this.store.createRecord('journal-remove-contact', {
      journalId: this.journalContactInfo.journalId,
      contactId: this.journalContactInfo.contactId,
      destroyPledge: this.cellState.removePledge,
    });
    await removeContact.save();
    this.removing = false;
    this.args.onRemoveJournalRow(this.args.journalRow);
  }
}
